import React, { useState, useEffect, useMemo } from 'react';
import { X, Save, RefreshCw } from 'lucide-react';
import api from '../../services/api';
import { previewRate } from '../../services/expenseService';
import ReceiptUpload from './ReceiptUpload';
import { CATEGORIES, CURRENCIES } from '../../utils/expenseOptions';

const ExpenseItemForm = ({ item, onSave, onCancel, receiptHref = null, rates = [], endDate = '' }) => {
    const [formData, setFormData] = useState({
        category: '',
        merchant: '',
        amount: '',
        currency: '',
        date: new Date().toISOString().split('T')[0],
        description: '',
        // Left undefined on purpose: not sent means "keep the stored receipt"; null means "remove it".
        ...item,
        // The server sends a timestamp; a date input only shows (and so only submits) yyyy-mm-dd.
        ...(item && item.date ? { date: String(item.date).split('T')[0] } : {}),
        // A line made from a receipt that showed no amount holds 0 until you type it: show an empty box.
        ...(item && item.reviewState === 'INPUT' && Number(item.amount) === 0 ? { amount: '' } : {}),
    });

    const [isSaving, setIsSaving] = useState(false);
    const [removedStored, setRemovedStored] = useState(false); // the stored receipt goes when this is saved

    const [baseCurrency, setBaseCurrency] = useState('INR');
    const [lookedUp, setLookedUp] = useState(null); // the rate for the report's end date, while the report has none for this currency

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const res = await api.get('/auth/config');
                const base = res.data.baseCurrency || 'INR';
                setBaseCurrency(base);
                // A new item starts in the company's own currency (not USD) until the receipt or the person says otherwise.
                setFormData(prev => (prev.currency ? prev : { ...prev, currency: base }));
            } catch (err) {
                console.error('Failed to fetch the company currency', err);
            }
        };
        fetchConfig();
    }, []);

    // One rate per currency for the whole report: the one the report already has, or, for a currency not used
    // yet, the rate on the report's end date (which becomes the report's rate when this line is saved).
    // The line's own date does not matter.
    const reportRate = rates.find(r => r.currency === formData.currency);
    useEffect(() => {
        setLookedUp(null);
        if (!formData.currency || formData.currency === baseCurrency || reportRate) return undefined;
        let stale = false; // an answer for a currency that is no longer chosen must not be shown
        const timer = setTimeout(async () => {
            try {
                const got = await previewRate(formData.currency, endDate);
                if (!stale) setLookedUp(got);
            } catch {
                if (!stale) setLookedUp({ failed: true });
            }
        }, 300);
        return () => { stale = true; clearTimeout(timer); };
    }, [formData.currency, baseCurrency, reportRate, endDate]);
    const rateInfo = formData.currency && formData.currency !== baseCurrency ? (reportRate || lookedUp) : null;

    const rateFailed = !!rateInfo?.failed;
    const convertedAmount = useMemo(() => {
        if (!formData.amount || !rateInfo?.rate) return null;
        return (parseFloat(formData.amount) * rateInfo.rate).toFixed(2);
    }, [formData.amount, rateInfo]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleReceiptProcessed = (ocrData) => {
        setFormData(prev => ({
            ...prev,
            merchant: ocrData.merchant || prev.merchant,
            category: prev.category || ocrData.category || '',
            amount: ocrData.amount || prev.amount,
            date: ocrData.date || prev.date,
            currency: ocrData.currency || prev.currency,
            receiptUrl: ocrData.receiptUrl
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.merchant || !formData.amount) {
            alert('Please fill in merchant and amount');
            return;
        }
        
        setIsSaving(true);
        try {
            await onSave({
                ...formData,
                convertedAmount: convertedAmount || formData.amount,
                baseCurrency: baseCurrency
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
        }}>
            <div style={{
                background: 'var(--surface)',
                borderRadius: 'var(--radius)',
                maxWidth: '600px',
                width: '100%',
                maxHeight: '90vh',
                overflow: 'auto',
                boxShadow: 'var(--shadow)'
            }}>
                <div style={{
                    padding: '1.5rem',
                    borderBottom: '1px solid var(--border)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <h2 style={{ margin: 0 }}>{item ? 'Edit Expense' : 'Add Expense'}</h2>
                    <button
                        onClick={onCancel}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '0.25rem'
                        }}
                    >
                        <X size={24} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} style={{ padding: '1.5rem' }}>
                    {item?.reviewNotes && (
                        <p role="status" style={{ marginBottom: '0.75rem', padding: '0.5rem 0.75rem', border: '1px solid #eab308', borderRadius: 'var(--radius)' }}>{item.reviewNotes}</p>
                    )}
                    {receiptHref && formData.receiptUrl === undefined && (
                        <p style={{ marginBottom: '0.75rem' }}>
                            A receipt is attached: <a href={receiptHref} target="_blank" rel="noreferrer">view it</a>.{' '}
                            Upload a new file below to replace it, or{' '}
                            <button type="button" className="btn btn-secondary" style={{ padding: '0.15rem 0.5rem' }} onClick={() => { setRemovedStored(true); setFormData(prev => ({ ...prev, receiptUrl: null })); }}>remove it</button>.
                        </p>
                    )}
                    {receiptHref && removedStored && formData.receiptUrl === null && (
                        <p role="status" style={{ marginBottom: '0.75rem' }}>
                            The receipt will be removed when you save.{' '}
                            <button type="button" className="btn btn-secondary" style={{ padding: '0.15rem 0.5rem' }} onClick={() => { setRemovedStored(false); setFormData(prev => ({ ...prev, receiptUrl: undefined })); }}>Undo</button>
                        </p>
                    )}
                    <ReceiptUpload onReceiptProcessed={handleReceiptProcessed} onReceiptCleared={() => setFormData(prev => ({ ...prev, receiptUrl: item?.hasReceipt && !removedStored ? undefined : null }))} />

                    <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                            Category *
                        </label>
                        <select
                            value={formData.category}
                            onChange={(e) => handleChange('category', e.target.value)}
                            required
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '1px solid var(--border)',
                                borderRadius: 'var(--radius)',
                                fontSize: '1rem'
                            }}
                        >
                            <option value="">Select category</option>
                            {CATEGORIES.map(cat => (
                                <option key={cat} value={cat}>{cat}</option>
                            ))}
                        </select>
                    </div>

                    <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                            Merchant *
                        </label>
                        <input
                            type="text"
                            value={formData.merchant}
                            onChange={(e) => handleChange('merchant', e.target.value)}
                            required
                            placeholder="e.g., Delta Airlines"
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '1px solid var(--border)',
                                borderRadius: 'var(--radius)',
                                fontSize: '1rem'
                            }}
                        />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                                Amount *
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                value={formData.amount}
                                onChange={(e) => handleChange('amount', e.target.value)}
                                required
                                placeholder="0.00"
                                style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    border: '1px solid var(--border)',
                                    borderRadius: 'var(--radius)',
                                    fontSize: '1rem'
                                }}
                            />
                        </div>

                        <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                                Currency
                            </label>
                            <select
                                value={formData.currency}
                                onChange={(e) => handleChange('currency', e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    border: '1px solid var(--border)',
                                    borderRadius: 'var(--radius)',
                                    fontSize: '1rem'
                                }}
                            >
                                {[...new Set([baseCurrency, ...CURRENCIES])].map(curr => (
                                    <option key={curr} value={curr}>{curr}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {rateFailed && formData.amount && (
                        <p role="status" style={{ marginBottom: '1rem', color: '#b45309', fontSize: '0.9rem' }}>
                            No exchange rate is available for {formData.currency} right now, so no estimate is shown. You can still save the expense: HR or Finance can set the rate.
                        </p>
                    )}
                    {convertedAmount && (
                        <div style={{
                            marginBottom: '1rem',
                            padding: '0.75rem',
                            background: '#eff6ff',
                            border: '1px solid #bfdbfe',
                            borderRadius: 'var(--radius)',
                            color: '#1e40af',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            fontSize: '0.9rem'
                        }}>
                            <RefreshCw size={16} />
                            <span>Approx. <strong>{baseCurrency} {convertedAmount}</strong></span>
                            <span style={{ fontSize: '0.75rem', opacity: 0.8 }}>
                                (1 {formData.currency} = {Number(rateInfo.rate).toFixed(4)} {baseCurrency}; {reportRate ? 'the rate of this report' : 'the rate for the report\'s end date, which becomes this report\'s rate'}{rateInfo.source === 'FALLBACK' ? '; approximate, the rate service could not be reached' : ''})
                            </span>
                        </div>
                    )}

                    <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                            Date *
                        </label>
                        <input
                            type="date"
                            value={formData.date}
                            onChange={(e) => handleChange('date', e.target.value)}
                            required
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '1px solid var(--border)',
                                borderRadius: 'var(--radius)',
                                fontSize: '1rem'
                            }}
                        />
                    </div>

                    <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                            Description
                        </label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => handleChange('description', e.target.value)}
                            placeholder="Optional notes about this expense"
                            rows={3}
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '1px solid var(--border)',
                                borderRadius: 'var(--radius)',
                                fontSize: '1rem',
                                resize: 'vertical'
                            }}
                        />
                    </div>

                    <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                        <button
                            type="button"
                            onClick={onCancel}
                            disabled={isSaving}
                            className="btn btn-secondary"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSaving}
                            className="btn btn-primary"
                        >
                            <Save size={16} style={{ marginRight: '0.5rem' }} />
                            {isSaving ? 'Saving...' : 'Save Expense'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ExpenseItemForm;
